import type {
  BuyerInventoryFiltersInput,
  InventoryStockHealth,
  NumberOperatorInput,
  StringOperatorInput,
} from '~/graphql/graphql';
import {
  buildAndFilter,
  buildOrFilter,
  type EnumOperator,
  type NumericOperator,
  type StringOperator,
  isComparisonFilterData,
  isOrdersFilterData,
  isRequestForQuoteFilterData,
} from '~/types';
import {
  filterRowsToSaved,
  readSavedFilterRows,
  textValueOf,
  type EditableFilterRow,
  type SavedFilterRow,
  type SavedFilterRowSchema,
} from '~/types/filters';
import {
  isBuyListFilterData,
  isMyBuyListFilterData,
} from '~/pages/buy-lists/lib/buyListFilterStorage';

export type InventoryFilterRow = EditableFilterRow;

export const INVENTORY_FILTER_DATA_KEY = 'buyerInventoryFilters';

export const INVENTORY_STRING_OPERATORS = [
  'contains',
  'eq',
  'neq',
  'notContain',
] as const;
export const INVENTORY_NUMBER_OPERATORS = [
  'gte',
  'gt',
  'lte',
  'lt',
  'eq',
  'neq',
] as const;
export const INVENTORY_ENUM_OPERATORS = ['eq', 'neq'] as const;

const OPERATORS_BY_FIELD: Record<string, readonly string[]> = {
  productName: INVENTORY_STRING_OPERATORS,
  productCode: INVENTORY_STRING_OPERATORS,
  length: INVENTORY_STRING_OPERATORS,
  location: INVENTORY_STRING_OPERATORS,
  branch: INVENTORY_STRING_OPERATORS,
  stock: INVENTORY_NUMBER_OPERATORS,
  usageRatePerWeek: INVENTORY_NUMBER_OPERATORS,
  status: INVENTORY_ENUM_OPERATORS,
};

export function operatorsForField(field: string): readonly string[] | null {
  return OPERATORS_BY_FIELD[field] ?? null;
}

const VALID_STATUS_VALUES: readonly string[] = ['HEALTHY', 'LOW', 'CRITICAL'];

const NUMBER_FIELDS = new Set(['stock', 'usageRatePerWeek']);

function toStringInput(op: StringOperator, value: string): StringOperatorInput {
  switch (op) {
    case 'contains':
      return { contains: value };
    case 'eq':
      return { eq: value };
    case 'neq':
      return { neq: value };
    case 'notContain':
      return { notContain: value };
  }
}

function toNumberInput(
  op: NumericOperator,
  value: number
): NumberOperatorInput {
  switch (op) {
    case 'eq':
      return { eq: value };
    case 'neq':
      return { neq: value };
    case 'gt':
      return { gt: value };
    case 'gte':
      return { gte: value };
    case 'lt':
      return { lt: value };
    case 'lte':
      return { lte: value };
  }
}

export function toClause(
  row: InventoryFilterRow
): BuyerInventoryFiltersInput | null {
  const value = textValueOf(row).trim();

  if (NUMBER_FIELDS.has(row.field)) {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return null;
    const numInput = toNumberInput(row.operator as NumericOperator, parsed);
    switch (row.field) {
      case 'stock':
        return { stock: numInput };
      case 'usageRatePerWeek':
        return { usageRatePerWeek: numInput };
      default:
        return null;
    }
  }

  if (row.field === 'status') {
    const statusValue = value as InventoryStockHealth;
    switch (row.operator as EnumOperator) {
      case 'eq':
        return { status: { eq: statusValue } };
      case 'neq':
        return { status: { neq: statusValue } };
    }
  }

  const strInput = toStringInput(row.operator as StringOperator, value);
  switch (row.field) {
    case 'productName':
      return { productName: strInput };
    case 'productCode':
      return { productCode: strInput };
    case 'length':
      return { length: strInput };
    case 'location':
      return { location: strInput };
    case 'branch':
      return { branchCode: strInput };
    default:
      return null;
  }
}

export function buildFiltersInput(
  rows: InventoryFilterRow[]
): BuyerInventoryFiltersInput {
  type ClauseWithConnector = {
    clause: BuyerInventoryFiltersInput;
    connector: 'and' | 'or';
  };

  const clausesWithConnectors: ClauseWithConnector[] = rows
    .filter((row) => textValueOf(row).trim() !== '')
    .flatMap((row) => {
      const clause = toClause(row);
      return clause ? [{ clause, connector: row.connector }] : [];
    });

  if (clausesWithConnectors.length === 0) return {};

  // Group consecutive AND-connected clauses; an OR connector starts a new group.
  const groups: BuyerInventoryFiltersInput[][] = [];
  let currentGroup: BuyerInventoryFiltersInput[] = [];

  for (const { clause, connector } of clausesWithConnectors) {
    if (connector === 'or' && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(clause);
  }
  groups.push(currentGroup);

  const groupInputs: BuyerInventoryFiltersInput[] = groups.map((group) =>
    group.length === 1
      ? group[0]
      : (buildAndFilter(group) as BuyerInventoryFiltersInput)
  );

  if (groupInputs.length === 1) return groupInputs[0];
  return buildOrFilter(groupInputs);
}

export function newInventoryFilterRowId(): string {
  return crypto.randomUUID();
}

export function activeRowCount(rows: InventoryFilterRow[]): number {
  return rows.filter(
    (row) => textValueOf(row).trim() !== '' && toClause(row) !== null
  ).length;
}

export function rowsToFilterData(rows: InventoryFilterRow[]): {
  [INVENTORY_FILTER_DATA_KEY]: SavedFilterRow[];
} {
  return { [INVENTORY_FILTER_DATA_KEY]: filterRowsToSaved(rows) };
}

const INVENTORY_SAVED_ROW_SCHEMA: SavedFilterRowSchema = {
  operatorsForField,
  isListField: () => false,
};

function hasKnownStatus(row: InventoryFilterRow): boolean {
  return (
    row.field !== 'status' || VALID_STATUS_VALUES.includes(textValueOf(row))
  );
}

export function readInventoryFilterRows(
  filterData: unknown
): InventoryFilterRow[] {
  if (typeof filterData !== 'object' || filterData === null) return [];
  const stored = (filterData as Record<string, unknown>)[
    INVENTORY_FILTER_DATA_KEY
  ];
  return readSavedFilterRows(stored, INVENTORY_SAVED_ROW_SCHEMA).filter(
    hasKnownStatus
  );
}

/** Every saved filter that no other page claims belongs to Inventory. */
export function isInventoryFilterData(filterData: unknown): boolean {
  return (
    !isOrdersFilterData(filterData) &&
    !isComparisonFilterData(filterData) &&
    !isBuyListFilterData(filterData) &&
    !isMyBuyListFilterData(filterData) &&
    !isRequestForQuoteFilterData(filterData)
  );
}
